import React, { useCallback, useMemo, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material//ArrowDropDown';
import FilterListIcon from '@mui/icons-material//FilterList';
import SearchIcon from '@mui/icons-material//Search';
import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { BulkActionsToolbarProps } from './BulkActionsToolbar.props';
import { Column } from '../../BulkActionsDrawer.types';
import { columnHeaders } from '../../constants';
import FundSearchMenu from '../FundSearchMenu/FundSearchMenu';

export const StyledInputAdornment = styled(InputAdornment)({
  height: '0.01em',
  display: 'flex',
  maxHeight: '2em',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  color: '#263238',
});

const BulkActionsToolbar = ({
  searchState: { value: searchQuery, setter: setSearchQuery },
  selectedFunds,
  fundSearchState: { value: fundSearchQuery, setter: setFundSearchQuery },
  filteredFunds,
  handleFundToggle,
  handleColumnToggle,
  handleSubColumnToggle,
  visibleColumns,
  visibleSubColumns,
}: BulkActionsToolbarProps) => {
  // Fund filter handlers

  const [fundFilterAnchorEl, setFundFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleFundFilterOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFundFilterAnchorEl(event.currentTarget);
  };

  const handleFundFilterClose = () => {
    setFundFilterAnchorEl(null);
  };

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchorEl(null);
  };

  const onSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setFundSearchQuery(value);
  };

  const unToggleableColumns = useMemo(() => {
    // if a column that has linkedVisibleColumns is visible, then we need a list
    const unToggleableColumnSet = new Set(
      columnHeaders
        .filter(column => column.linkedVisibleColumns && visibleColumns.includes(column.field))
        .flatMap(column => column.linkedVisibleColumns)
    );
    return Array.from(unToggleableColumnSet);
  }, [visibleColumns]);

  const onSubColumnToggle = useCallback((subColumn: Column, parentColumn: Column) => {
    handleSubColumnToggle(subColumn.field, parentColumn.field);
  }, [handleSubColumnToggle])

  const onToggle = useCallback(
    (column: Column) => {
      if (unToggleableColumns.includes(column.field)) {
        return;
      }
      handleColumnToggle(column.field);
      column.linkedVisibleColumns.forEach(linkedVisibleColumn => {
        //   only toggle linked columns if they are not already visible
        if (visibleColumns.includes(linkedVisibleColumn)) {
          return;
        }
        handleColumnToggle(linkedVisibleColumn);
      });
    },
    [handleColumnToggle, unToggleableColumns, visibleColumns]
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
      <TextField
        variant="standard"
        placeholder="Search company..."
        fullWidth
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <StyledInputAdornment position="start">
                <SearchIcon />
              </StyledInputAdornment>
            ),
          },
        }}
        style={{ marginRight: 16 }}
      />

      {/* Fund Filter Button */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={handleFundFilterOpen}
          style={{ marginRight: 8, color: '#263238' }}
          aria-label="filter by fund">
          <FilterListIcon />
        </IconButton>
        {selectedFunds.size > 0 && (
          <Typography variant="body2" style={{ marginRight: 8 }}>
            {selectedFunds.size} Fund{selectedFunds.size !== 1 ? 's' : ''} selected
          </Typography>
        )}
      </div>

      {/* Fund Filter Menu */}
      <FundSearchMenu
        handleFundToggle={handleFundToggle}
        handleFundFilterClose={handleFundFilterClose}
        fundSearchQuery={fundSearchQuery}
        onSearchQueryChange={onSearchQueryChange}
        fundFilterAnchorEl={fundFilterAnchorEl}
        selectedFunds={selectedFunds}
        filteredFunds={filteredFunds}
      />

      <Button
        variant="text"
        onClick={handleColumnMenuOpen}
        endIcon={<ArrowDropDownIcon />}
        style={{
          borderRadius: 4,
          textTransform: 'none',
          justifyContent: 'space-between',
          padding: '6px 12px',
          minWidth: '160px',
          color: '#263238',
        }}>
        More Options
      </Button>
      <Menu anchorEl={columnMenuAnchorEl} open={Boolean(columnMenuAnchorEl)} onClose={handleColumnMenuClose}>
        {columnHeaders
          .filter(column => column.field !== 'company_name')
          .flatMap(column => [
            <MenuItem key={column.field} onClick={() => onToggle(column)}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={visibleColumns.includes(column.field)}
                  tabIndex={-1}
                  disabled={unToggleableColumns.includes(column.field)}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={column.headerName} />
            </MenuItem>,
            ...(column.subColumns ?? []).map(subColumn => (
              <MenuItem
                key={subColumn.field}
                style={{ paddingLeft: 32 }}
                onClick={
                  subColumn.isEditable && visibleColumns.includes(column.field)
                    ? () => onSubColumnToggle(subColumn, column)
                    : undefined
                }>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={
                      visibleSubColumns.find(
                        subCol => subCol.field === subColumn.field && subCol.parentField === column.field
                      ) !== undefined
                    }
                    tabIndex={-1}
                    disabled={!(subColumn.isEditable && visibleColumns.includes(column.field))}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={subColumn.headerName} />
              </MenuItem>
            ))
          ])}
      </Menu>
    </div>
  );
};

export default BulkActionsToolbar;
